'use client';
import styled, { css, keyframes } from 'styled-components';

const SPRING_TRANSLATE_EASING = `linear(
  0,
  0.0188,
  0.0679,
  0.1374,
  0.2195,
  0.308,
  0.3978,
  0.4856,
  0.5686,
  0.6452,
  0.7142,
  0.7753,
  0.8283,
  0.8735,
  0.9113,
  0.9423,
  0.9671,
  0.9866,
  1.0014,
  1.0123,
  1.0198,
  1.0247,
  1.0274,
  1.0283,
  1.0281,
  1.0268,
  1.025,
  1.0227,
  1.0202,
  1.0177,
  1.0152,
  1.0128,
  1.0106,
  1.0085,
  1.0068,
  1.0052,
  1.0039,
  1.0028,
  1.0018,
  1.0011,
  1.0005,
  1,
  0.9997,
  0.9995,
  0.9993,
  0.9992,
  0.9992,
  0.9992,
  0.9992,
  0.9993,
  0.9993
)`;

const SPRING_SCALE_EASING = `linear(
  0,
  0.03,
  0.1077,
  0.2165,
  0.342,
  0.4728,
  0.6001,
  0.7175,
  0.8208,
  0.9077,
  0.9776,
  1.0307,
  1.0684,
  1.0925,
  1.1051,
  1.1084,
  1.1047,
  1.096,
  1.084,
  1.0703,
  1.0561,
  1.0424,
  1.0297,
  1.0187,
  1.0094,
  1.0019,
  0.9963,
  0.9923,
  0.9898,
  0.9885,
  0.9882,
  0.9887,
  0.9897,
  0.991,
  0.9925,
  0.994,
  0.9955,
  0.9969,
  0.9981,
  0.9991,
  0.9998,
  1.0004,
  1.0009,
  1.0011,
  1.0012,
  1.0013,
  1.0012,
  1.0011,
  1.001,
  1.0008,
  1.0006
)`;

// Desktop: starts immediately, drops from 200px
const notificationOverlayOpacityDesktop = keyframes`
  0% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

const notificationOverlayTranslateDesktop = keyframes`
  0% {
    animation-timing-function: ${SPRING_TRANSLATE_EASING};
    translate: 0px 200px;
  }
  25.02% {
    translate: 0px 0px;
  }
  100% {
    translate: 0px 0px;
  }
`;

const notificationOverlayScaleDesktop = keyframes`
  0% {
    animation-timing-function: ${SPRING_SCALE_EASING};
    scale: 0.6 0.6;
  }
  15% {
    scale: 1 1;
  }
  100% {
    scale: 1 1;
  }
`;

// Mobile: holds until ~27%, drops from 100px
const notificationOverlayOpacityMobile = keyframes`
  0% {
    opacity: 0;
  }
  27% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 0;
  }
  47% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

const notificationOverlayTranslateMobile = keyframes`
  0% {
    translate: 0px 100.004px;
  }
  27% {
    animation-timing-function: ${SPRING_TRANSLATE_EASING};
    translate: 0px 100.004px;
  }
  52% {
    translate: 0px 0px;
  }
  100% {
    translate: 0px 0px;
  }
`;

const notificationOverlayScaleMobile = keyframes`
  0% {
    scale: 0.6 0.6;
  }
  27% {
    animation-timing-function: ${SPRING_SCALE_EASING};
    scale: 0.6 0.6;
  }
  42% {
    scale: 1 1;
  }
  100% {
    scale: 1 1;
  }
`;

const animatingStyles = delay => css`
  opacity: 0;
  translate: 0px 100.004px;
  scale: 0.6 0.6;
  animation: ${notificationOverlayOpacityMobile} 5s linear,
    ${notificationOverlayTranslateMobile} 5s linear,
    ${notificationOverlayScaleMobile} 5s linear;
  animation-iteration-count: 1;
  animation-fill-mode: both;
  animation-delay: ${delay || 0}ms;

  @media ${props => props.theme.tablet} {
    translate: 0px 200px;
    animation: ${notificationOverlayOpacityDesktop} 5s linear,
      ${notificationOverlayTranslateDesktop} 5s linear,
      ${notificationOverlayScaleDesktop} 5s linear;
    animation-iteration-count: 1;
    animation-fill-mode: both;
    animation-delay: ${delay || 0}ms;
  }
`;

export const Overlay = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  z-index: 20;
  overflow: hidden;
`;

export const NotificationStack = styled.div`
  position: absolute;
  top: 4%;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing(2.5)};
  width: 100%;
  box-sizing: border-box;
  padding: 0 ${props => props.theme.spacing(3)};

  @media ${props => props.theme.tablet} {
    top: 6%;
    left: ${props => (props.$alignment === 'left' ? '3%' : 'unset')};
    right: ${props => (props.$alignment === 'right' ? '3%' : 'unset')};
    width: max-content;
    padding: 0;
    align-items: ${props =>
      props.$alignment === 'left' ? 'flex-start' : 'flex-end'};
  }
`;

export const StyledNotificationCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(3)};
  width: 100%;
  max-width: 360px;
  box-sizing: border-box;
  padding: ${props => props.theme.spacing(3)};
  background-color: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: ${props => props.theme.spacing(5)};
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08);
  will-change: opacity, translate, scale;

  @media ${props => props.theme.tablet} {
    width: 360px;
  }

  ${props => {
    switch (props.$animationState) {
      case 'animating':
        return animatingStyles(props.$delay);
      case 'settled':
        return css`
          opacity: 1;
          translate: 0px 0px;
          scale: 1 1;
        `;
      default:
        return css`
          opacity: 0;
          translate: 0px 100.004px;
          scale: 0.6 0.6;

          @media ${props => props.theme.tablet} {
            translate: 0px 200px;
          }
        `;
    }
  }}
`;

export const IconContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: ${props => props.theme.spacing(2)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

export const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(0.5)};
  min-width: 0;
`;

export const Subject = styled.p`
  margin: 0;
  ${props => props.theme.typography[props.$typography]};
  color: ${props =>
    props.theme.colors.get(props.$fontColor, props.theme.colors.text.primary)};
`;

export const Message = styled.p`
  margin: 0;
  ${props => props.theme.typography[props.$typography]};
  color: ${props =>
    props.theme.colors.get(
      props.$fontColor,
      props.theme.colors.text.secondary,
    )};
`;
